import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownLong, faArrowLeft, faArrowRight, faArrowUpLong } from "@fortawesome/pro-solid-svg-icons";
import Spin from "components/spin/spin";
import { ButtonNew as Button } from "components/buttonNew/index";
import Card from "components/card/Card";
import "./dataTable.scss";
import { useTranslation } from "react-i18next";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndDataTableContext } from "./DndDataTableContext";
import { displayCurrency } from "helpers/Helpers";

/**
 * - 'clickable' className on < tr /> element enables click-styling.
 * - 'no-border' className on < td /> element removes bottom border from table cell.
 * @param {Array<HeaderType<T>>} columns header obj array to define the table header cells.
 * @param {string} title Title added to the wrapper.
 * @param {React.ReactNode} children rendered as child of < tbody />.  Map through data and display within <tr >'s.
 * @param {string} className optional styling attached to table element.
 * @param {(key: keyof T, direction: "asc" | "desc") => void} sort optional column sort functionality added to table headers.
 * @param {boolean} loading render table spinner instead of the data.
 * @param {{}} footer optionally render table footer.  enables pagination functionality on table.
 * @param {boolean} enableDragAndDrop optionally enable drag and drop.
 */
type DataTableProps<T> = {
  columns: Array<HeaderType<T>>;
  secondaryColumns?: Array<HeaderType<T>>;
  title?: string | React.ReactNode;
  children?: React.ReactNode;

  className?: string;
  hideHeader?: boolean;
  stickyHeader?: boolean;
  sort?: (key: keyof T, direction: "asc" | "desc") => void;
  /** Initial sort direction applied on mount when sort is enabled. Defaults to "asc". */
  defaultSortDirection?: "asc" | "desc";
  loading?: boolean;
  footer?: {
    tableLimit: number;
    tableOffset: number;
    handleTableOffset: (direction: "prev" | "next") => void;
    disableNextOffset?: boolean;
  };
  footerTotals?: {
    currency?: string;
    subtotal?: number;
    balance?: number;
    taxLines?: Array<{ name: string; total: number; rate: number }>;
    totalCost?: number;
    quantity?: number;
    totalCostLabel?: string;
    quantityLabel?: string;
  };
  enableDragAndDrop?: boolean;
  textWrap?: boolean;
  overflow?: boolean;
  noRoundedTop?: boolean;
  tableBodyClassName?: string;
  ariaBusy?: boolean;
};

export type HeaderType<T> = {
  /** required if wanting column sort */
  id?: keyof T | null;
  label: string;
  /** If given, takes precedence over rendering the label */
  content?: React.FC | JSX.Element;
  /** Specify a specific width of the table cells.  Default = "auto" */
  width?: string;
  /** Specify text alignment of the header data.  Default = "left" */
  justify?: "center" | "right";

  padding?: string;
};

export default function DataTable<T>(props: DataTableProps<T>): JSX.Element {
  const {
    columns,
    secondaryColumns,
    footer,
    children,
    loading = false,
    sort,
    defaultSortDirection = "asc",
    hideHeader = false,
    enableDragAndDrop = false,
    textWrap = false,
    overflow = false,
    footerTotals = false,
    noRoundedTop = false,
    tableBodyClassName,
    ariaBusy,
  } = props;

  const [columnSortDirection, setColumnSortDirection] = useState<{ [x: string]: "asc" | "desc" }>(() => {
    const initialColumn = columns?.[0];
    if (sort && !enableDragAndDrop && initialColumn?.id && !initialColumn.content) {
      return { [initialColumn.id as string]: defaultSortDirection };
    }
    return {};
  });
  const { t } = useTranslation();
  //default sort on first || second column

  const sortEnabled = sort && !enableDragAndDrop;
  const footerEnabled = footer && !enableDragAndDrop;
  const footerTotalsEnabled = !!footerTotals;
  const tfootEnabled = footerEnabled || footerTotalsEnabled;

  const computedColumns = useMemo(() => {
    if (enableDragAndDrop) {
      return [{ label: "", width: "fit-content", padding: "0px" }, ...columns];
    } else {
      return columns;
    }
  }, [columns, enableDragAndDrop]);

  useEffect(() => {
    if (!sortEnabled) {
      return;
    }

    if (!computedColumns[0].content) {
      sort(computedColumns[0].id, defaultSortDirection);
      setColumnSortDirection({ [computedColumns[0].id as string]: defaultSortDirection });
    } else if (!computedColumns[1]?.content && computedColumns[1]?.id) {
      sort(computedColumns[1].id, defaultSortDirection);
      setColumnSortDirection({ [computedColumns[1].id as string]: defaultSortDirection });
    }
  }, []);

  function handleColumnSort(colProp: any, direction: "asc" | "desc") {
    setColumnSortDirection({ [colProp]: direction });
    sort(colProp as keyof T, direction);
  }

  return (
    <Card style={{ border: 0, overflowY: "visible" }} title={props.title}>
      <table
        style={{ whiteSpace: textWrap ? "unset" : undefined, overflow: overflow ? "visible" : "hidden" }}
        className={`data-table ${props.className ? props.className : ""}${props.stickyHeader ? " sticky-header" : ""}${noRoundedTop ? " no-rounded-top" : ""
          }`}
        aria-busy={ariaBusy ? true : undefined}
      >
        {/* Render header label | action element. */}
        {!hideHeader ? (
          <thead className="data-thead">
            <tr>
              {computedColumns.map((col, index) => {
                const identifier = col.id
                  ? (col.id as string)
                  : col.label.length > 0
                    ? col.label.concat("_null")
                    : `empty_col_${index}`;
                const colName = !col.content ? identifier : col.label.concat("_action");
                const colProp = !col.content && col.id ? (col.id as string) : col.label; // ex. "processed_at" vs "Processed At"

                return (
                  <th key={colName} style={{ width: col.width ?? "auto", padding: col.padding ?? "" }}>
                    <div
                      className="th-wrap"
                      style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center",
                        justifyContent: col.justify ?? "left",
                      }}
                    >
                      {!col.content ? <p className="table-label">{col.label}</p> : <>{col.content}</>}
                      {sortEnabled && (
                        <>
                          {columnSortDirection[colProp] === "asc" && (
                            <FontAwesomeIcon
                              onClick={() => handleColumnSort(colProp, "desc")}
                              icon={faArrowUpLong}
                              className="sort-arrow-up"
                              size="xs"
                            />
                          )}
                          {columnSortDirection[colProp] === "desc" && (
                            <FontAwesomeIcon
                              onClick={() => handleColumnSort(colProp, "asc")}
                              icon={faArrowDownLong}
                              className="sort-arrow-down"
                              size="xs"
                            />
                          )}
                          {!columnSortDirection[colProp] &&
                            !colName?.includes("_action") &&
                            !colName.includes("empty") &&
                            !colName.includes("_null") && (
                              // Undefined direction && Not an action && identifier is not empty or null
                              <FontAwesomeIcon
                                onClick={() => handleColumnSort(colProp, "asc")}
                                icon={["far", "arrow-up-arrow-down"]}
                                className="sort-arrow-both"
                                size="sm"
                              />
                            )}
                        </>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
            {secondaryColumns ? (
              <tr>
                {secondaryColumns.map((col, index) => {
                  const identifier = col.id
                    ? (col.id as string)
                    : col.label.length > 0
                      ? col.label.concat("_null")
                      : `empty_col_${index}`;
                  const colName = !col.content ? identifier : col.label.concat("_action");
                  const colProp = !col.content && col.id ? (col.id as string) : col.label; // ex. "processed_at" vs "Processed At"

                  return (
                    <th key={colName} style={{ width: col.width ?? "auto", padding: col.padding ?? "" }}>
                      <div
                        className="th-wrap"
                        style={{
                          display: "flex",
                          gap: "6px",
                          alignItems: "center",
                          justifyContent: col.justify ?? "left",
                        }}
                      >
                        {!col.content ? <p className="table-label">{col.label}</p> : <>{col.content}</>}
                        {/* Only display sort icons if sort callback given. */}
                        {sort && (
                          <>
                            {columnSortDirection[colProp] === "asc" && (
                              <FontAwesomeIcon
                                onClick={() => handleColumnSort(colProp, "desc")}
                                icon={faArrowUpLong}
                                className="sort-arrow-up"
                              />
                            )}
                            {columnSortDirection[colProp] === "desc" && (
                              <FontAwesomeIcon
                                onClick={() => handleColumnSort(colProp, "asc")}
                                icon={faArrowDownLong}
                                className="sort-arrow-down"
                              />
                            )}
                            {!columnSortDirection[colProp] &&
                              !colName?.includes("_action") &&
                              !colName.includes("empty") &&
                              !colName.includes("_null") && (
                                // Undefined direction && Not an action && identifier is not empty or null
                                <FontAwesomeIcon
                                  onClick={() => handleColumnSort(colProp, "asc")}
                                  icon={["far", "arrow-up-arrow-down"]}
                                  className="sort-arrow-both"
                                />
                              )}
                          </>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ) : null}
          </thead>
        ) : null}

        {/* Render <Spin /> | children | null */}
        <DndProvider backend={HTML5Backend}>
          <DndDataTableContext.Provider
            value={{
              enableDragAndDrop,
            }}
          >
            <tbody
              className={`${!tfootEnabled ? "no-footer" : ""}${hideHeader ? " header-hidden" : ""}${tableBodyClassName ? ` ${tableBodyClassName}` : ""
                }`}
            >
              {loading ? (
                <tr>
                  <td colSpan={computedColumns.length} className="td-spinner">
                    <Spin />
                  </td>
                </tr>
              ) : (
                <>{children ? children : null}</>
              )}
            </tbody>
          </DndDataTableContext.Provider>
        </DndProvider>

        {/* Enables pagination functionality of table data + optional totals. */}
        {(footerEnabled || footerTotalsEnabled) && (
          <tfoot>
            {footerEnabled && (
              <tr>
                <td colSpan={computedColumns.length}>
                  <div className="flex justify-between">
                    <Button
                      size="small"
                      type="secondary"
                      onClick={() => {
                        props.footer.handleTableOffset("prev");
                      }}
                      disabled={!(props.footer.tableLimit <= props.footer.tableOffset)}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                      &nbsp;
                      {t("Previous")}
                    </Button>


                    <Button
                      size="small"
                      type="secondary"
                      onClick={() => {
                        props.footer.handleTableOffset("next");
                      }}
                      disabled={props.footer.disableNextOffset}
                    >
                      {t("Next")}
                      &nbsp;
                      <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                  </div>
                </td>
              </tr>
            )}

            {footerTotalsEnabled && (
              <tr>
                <td colSpan={computedColumns.length > 4 ? 2 : 1}></td>

                {computedColumns.length - 2 === 0 ? null : (
                  <td colSpan={computedColumns.length > 4 ? computedColumns.length - 4 : computedColumns.length - 2} />
                )}

                <td colSpan={computedColumns.length > 4 ? 2 : 1}>
                  <div style={{ display: "flex", justifyContent: "flex-end", flexDirection: "column" }}>
                    {footerTotals?.totalCost !== undefined && (
                      <div className="flex justify-between">
                        <p className="footer-total-label">{footerTotals.totalCostLabel ?? t("Total Cost")}:</p>
                        <p className="footer-total-value">
                          {displayCurrency("CAD", footerTotals.totalCost)} {footerTotals?.currency?.toUpperCase()}
                        </p>
                      </div>
                    )}

                    {footerTotals?.quantity !== undefined && (
                      <div className="flex justify-between">
                        <p className="footer-total-label">{footerTotals.quantityLabel ?? t("Quantity")}:</p>
                        <p className="footer-total-value">{footerTotals.quantity}</p>
                      </div>
                    )}

                    {footerTotals?.subtotal !== undefined && (
                      <div className="flex justify-between">
                        <p className="footer-total-label">{t("Subtotal")}:</p>
                        <p className="footer-total-value">
                          {displayCurrency("CAD", footerTotals.subtotal)} {footerTotals?.currency?.toUpperCase()}
                        </p>
                      </div>
                    )}

                    {footerTotals?.taxLines?.map((tax, index) => {
                      return (
                        <div key={index} className="flex justify-between">
                          <p className="footer-total-label">
                            {tax.name} ({tax.rate * 100}%)
                          </p>
                          <p className="footer-total-value">
                            {displayCurrency("CAD", tax.total)} {footerTotals?.currency?.toUpperCase()}
                          </p>
                        </div>
                      );
                    })}

                    {footerTotals?.balance !== undefined && (
                      <div className="flex justify-between">
                        <p className="footer-total-label">{t("Balance due")}:</p>
                        <p className="footer-total-value">
                          {displayCurrency("CAD", footerTotals.balance)} {footerTotals?.currency?.toUpperCase()}
                        </p>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tfoot>
        )}
      </table>
    </Card>
  );
}
